/**
 * Lazy Firebase accessor.
 *
 * WHY THIS FILE HAS NO MODULE-SCOPE FIREBASE:
 *
 * `@firebase/*` measured 112,941 bytes of the eagerly-loaded vendor chunk
 * (`79.*.chunk.js` before this change) — auth 82,446, app 12,203, util 9,833,
 * component 4,839, logger 2,901, firebase 719. Every Firebase call site is
 * user-initiated (sign-in, sign-up, sign-out, password change, and the
 * `onIdTokenChanged` refresh listener that only attaches once a profile
 * already exists — `MainLayout.js:441`). An anonymous visitor never touches
 * Firebase, yet used to pay for it on first paint.
 *
 * This module previously ran `initializeApp(firebaseConfig)` and `getAuth(app)`
 * at module scope, so merely importing it pulled the whole SDK into the entry
 * graph *and* initialised Firebase during `renderToString` — which is why the
 * SSR layer crashed when `REACT_APP_FIREBASE_API` was absent. Both problems are
 * the same problem. **Do not reintroduce module-scope initialisation.**
 *
 * @see work/21c-frontend-critical-path/design.md — change 6(a)
 */

// Firebase-free by design — see the header of that file. Importing it
// statically adds no Firebase to the eager graph.
import { activateAppCheck } from './firebase-app-check';

/**
 * Thrown when the Firebase chunk cannot be fetched or the SDK cannot be
 * initialised. `code` deliberately mimics Firebase's own `auth/*` code shape so
 * it flows through the existing UI translation path
 * (`translate('ERROR.AUTH.' + error.code)` in `LoginForm.js:94`,
 * `SigninForm.js:129`) with a real message instead of a raw
 * "Loading chunk N failed" string.
 */
export class FirebaseUnavailableError extends Error {
    /**
     * @param {string} message - English fallback shown if the lang key is missing.
     * @param {Error} [cause] - The underlying import/init failure, kept for reporting.
     */
    constructor(message, cause) {
        super(message);
        this.name = 'FirebaseUnavailableError';
        this.code = 'auth/module-load-failed';
        this.cause = cause;
    }
}

/**
 * Memoised in-flight/settled load. Cleared on failure so that a second user
 * attempt (pressing "sign in" again) retries rather than replaying a cached
 * rejection — chunk fetch failures are usually transient (flaky network, or a
 * deploy that rotated the hashed chunk filename mid-session).
 * @type {Promise<{ auth: object, app: object, authApi: object }>|null}
 */
let loadPromise = null;

/**
 * Firebase web config. Read at call time, not at module load, so a missing
 * env var cannot crash an SSR render.
 * @returns {object} the config object passed to `initializeApp`.
 */
function readConfig() {
    return {
        apiKey: process.env.REACT_APP_FIREBASE_API,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };
}

/**
 * Perform the actual dynamic import + initialisation. Separated from
 * `loadFirebaseAuth` so the memoisation and its failure reset stay readable.
 *
 * @returns {Promise<{ auth: object, app: object, authApi: object }>}
 * @throws {FirebaseUnavailableError} if config is missing, the chunk fails to
 *   download, or `initializeApp`/`getAuth` throws.
 */
async function initFirebaseAuth() {
    const firebaseConfig = readConfig();

    // Fail fast and legibly rather than letting the SDK throw
    // `auth/invalid-api-key` several frames deeper.
    if (!firebaseConfig.apiKey) {
        throw new FirebaseUnavailableError(
            'Firebase is not configured (REACT_APP_FIREBASE_API is missing).'
        );
    }

    let authApi;
    try {
        // CLIENT-ONLY. This `import()` must never be awaited during SSR.
        // `server/index.js` registers `babel-plugin-dynamic-import-node`, so on
        // the server an `import()` becomes a synchronous `require()` and would
        // pull Firebase's **Node** build, which behaves differently from the
        // browser build (no browser persistence, different popup and redirect
        // handling). Every caller of `loadFirebaseAuth` is reached only from a
        // user gesture or from `componentDidUpdate`, neither of which runs
        // during `renderToString`.
        //
        // It targets `./firebase-api` rather than `firebase/auth` so webpack can
        // tree-shake the SDK down to the symbols actually used — see the header
        // of that file; it is worth 42 KB.
        authApi = await import(/* webpackChunkName: "firebase-auth" */ './firebase-api');
    } catch (error) {
        throw new FirebaseUnavailableError(
            'Could not load the sign-in module. Please check your connection and try again.',
            error
        );
    }

    let app;
    try {
        app = authApi.initializeApp(firebaseConfig);
    } catch (error) {
        throw new FirebaseUnavailableError(
            'Could not start the sign-in module. Please try again.',
            error
        );
    }

    // App Check (monitoring mode). Two things about this placement are
    // deliberate and neither is cosmetic.
    //
    // (a) BEFORE `getAuth`, matching `bt-admin/src/core/config/firebase.ts`.
    //     Auth almost certainly resolves the App Check provider lazily, when it
    //     builds request headers, rather than when `getAuth` returns — in which
    //     case activating afterwards would work too. But that is an unverified
    //     claim about SDK internals, and if it is wrong the storefront reports
    //     0% verified, which is the entire product of this change. Registering
    //     first removes the question instead of relying on the answer.
    //
    // (b) OUTSIDE the try/catch above, and in its own statement, so that it can
    //     never be mistaken for a sign-in failure. `activateAppCheck` is
    //     contractually incapable of throwing or rejecting; if it is
    //     unconfigured or fails it returns `null` and sign-in continues exactly
    //     as before. It is awaited rather than fired-and-forgotten so the
    //     provider is registered before any Auth request exists; the await
    //     costs nothing, as `initializeAppCheck` is synchronous and the
    //     reCAPTCHA script loads in the background.
    //
    // It runs on the same lazy, user-initiated path as the rest of Firebase, so
    // it is off first paint and off SSR: the only route here is the dynamic
    // `import()` above, which never executes during `renderToString`.
    await activateAppCheck({ app, authApi });

    let auth;
    try {
        auth = authApi.getAuth(app);
    } catch (error) {
        throw new FirebaseUnavailableError(
            'Could not start the sign-in module. Please try again.',
            error
        );
    }

    return { app, auth, authApi };
}

/**
 * Load, initialise (once) and return the Firebase auth handles.
 *
 * Callers get both the `Auth` instance and the Firebase API surface, so that no
 * file needs a static `import ... from 'firebase/auth'` — a single static import
 * anywhere in the eager graph would drag the SDK straight back into the vendor
 * chunk and undo this change.
 *
 * @returns {Promise<{ auth: object, app: object, authApi: object }>} `auth` is
 *   the initialised `Auth` instance, `app` the `FirebaseApp`, and `authApi` the
 *   `assets/firebase-api` namespace (`signOut`, `signInWithPopup`, …). If a
 *   symbol is missing from `authApi`, add it to `assets/firebase-api.js`.
 * @throws {FirebaseUnavailableError} when the chunk cannot be loaded or the SDK
 *   cannot be initialised. Callers must surface this to the user — it is the
 *   login path, and a silent failure looks like a hang.
 */
export function loadFirebaseAuth() {
    if (!loadPromise) {
        loadPromise = initFirebaseAuth().catch((error) => {
            loadPromise = null; // allow a retry on the user's next attempt
            throw error;
        });
    }
    return loadPromise;
}

export default loadFirebaseAuth;
